import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiShield, FiZap, FiTrendingUp, FiUsers,
  FiMail, FiBarChart2, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import HomeHero from '../components/HomeHero';
import './pages.css';
import dashboard   from '../images/dashboard.png';
import analytics   from '../images/analytics.png';
import team        from '../images/team.png';
import hmrc        from '../images/hmrc-filing.png';

const Home = () => {
  const [testimonialsIndex, setTestimonialsIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const carouselRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  const features = [
    { icon: FiShield,    title: 'Secure & Compliant',  description: 'Full HMRC and GDPR compliance across payroll, VAT, and every financial record, with enterprise-grade security throughout.', color: '#0ea5e9' },
    { icon: FiZap,       title: 'One Platform, Every Function', description: 'Payroll, HR, invoicing, accounts payable, banking, accounting, VAT, and appointments — all in one place instead of a dozen disconnected tools.', color: '#10b981' },
    { icon: FiTrendingUp,title: 'Real-Time Reporting',  description: 'Live profit & loss, balance sheet, cash position, and aging reports computed straight from your ledger, not a stale export.', color: '#f59e0b' },
    { icon: FiUsers,     title: 'Team-Ready',          description: 'Invite your team with role-based access, custom approval workflows, and document sign-off built in from day one.', color: '#ec4899' },
    { icon: FiMail,      title: 'Marketing Included',   description: 'Manage subscriber lists and send real email campaigns without a separate marketing tool.', color: '#8b5cf6' },
    { icon: FiBarChart2, title: 'Grows With You',      description: 'Start on a free trial and scale up as you need more — accounting, VAT, appointments, and workflow automation unlock as you grow.', color: '#06b6d4' }
  ];

  const showcaseImages = [
    { src: dashboard, label: 'Dashboard Overview',  description: 'A single dashboard covering payroll, invoicing, accounts payable, banking, and everything in between — no more switching between tools.' },
    { src: analytics, label: 'Analytics & Reports', description: 'Real-time profit & loss, balance sheet, and aging reports, computed live from your accounts and exportable when you need them.' },
    { src: team,      label: 'Team Management',     description: 'Manage your entire workforce — employees, leave, recruitment, and onboarding — with role-based access and permissions.' },
    { src: hmrc,      label: 'HMRC Filing',         description: 'Payroll RTI submissions alongside VAT filing, both tracked in one place with full compliance built in.' },
  ];

  const testimonials = [
    { name: 'Sarah Johnson', company: 'TechStart Ltd',    text: "Effective Global Staff replaced four separate tools for us — payroll, invoicing, and accounting all in one place.", rating: 5, initials: 'SJ' },
    { name: 'Michael Chen',  company: 'Global Solutions', text: 'The approval workflows and automated reminders alone save us hours every month. Genuinely built for how we actually work.', rating: 5, initials: 'MC' },
    { name: 'Emma Williams', company: 'Creative Agency',  text: "From payroll to VAT returns to client invoicing — it's the first platform that's actually kept up with the whole business.", rating: 5, initials: 'EW' }
  ];

  const videos = [
    { id: 'BnRmog4qTt0', title: 'UK Payroll Basics for Small Business', description: 'Learn the fundamentals of UK payroll management for small businesses.' },
    { id: 'yhnpUsixdhs', title: 'HMRC First Assessment Guide',         description: 'Complete guide to HMRC First Assessment Guide' },
    { id: 'hpRt4kt7jnU', title: 'How UK Payroll Works',                description: 'Understanding the core principles of UK payroll management.' }
  ];

  /* ── Professional bodies / accreditations ─────────────────────────────── */
  const bodies = [
    { name: 'CIPP',            full: 'Chartered Institute of Payroll Professionals',  logo: '/images/cipp.png',            url: 'https://www.cipp.org.uk' },
    { name: 'HMRC',            full: 'HM Revenue & Customs Recognised',                  logo: '/images/hmrc.png',            url: 'https://www.gov.uk/government/organisations/hm-revenue-customs' },
    { name: 'ICO',             full: 'Information Commissioner\'s Office Registered',  logo: '/images/ico.png',             url: 'https://ico.org.uk' },
    { name: 'GDPR',            full: 'UK GDPR Compliant',                               logo: '/images/gdpr.png',            url: 'https://www.gov.uk/data-protection' },
    { name: 'ISO 27001',       full: 'ISO 27001 Certified',                              logo: '/images/iso27001.png',        url: 'https://www.iso.org/standard/27001' },
    { name: 'FCA',             full: 'Financial Conduct Authority Registered',         logo: '/images/fca.png',             url: 'https://www.fca.org.uk' },
    { name: 'BACS',            full: 'Bacs Approved Bureau',                             logo: '/images/bacs.png',            url: 'https://www.bacs.co.uk' },
    { name: 'Cyber Essentials', full: 'Cyber Essentials Certified',                     logo: '/images/cyber-essentials.png', url: 'https://www.ncsc.gov.uk/cyberessentials/overview' },
    { name: 'SOC 2',           full: 'SOC 2 Type II Audited',                            logo: '/images/soc2.png',            url: 'https://www.aicpa-cima.com/topic/audit-assurance/audit-and-assurance-greater-than-soc-2' },
    { name: 'RTI',             full: 'HMRC RTI Approved Software',                     logo: '/images/rti.png',             url: 'https://www.gov.uk/running-payroll/reporting-to-hmrc' },
  ];

  /* Duplicate for infinite scroll */
  const bodiesLoop = [...bodies, ...bodies];

  /* Auto-scroll the carousel */
  useEffect(() => {
    if (isPaused) return;
    const el = carouselRef.current;
    if (!el) return;
    let frame;
    const step = () => {
      el.scrollLeft += 0.6;
      if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [isPaused]);

  const nextTestimonial = () => setTestimonialsIndex(p => (p + 1) % testimonials.length);
  const prevTestimonial = () => setTestimonialsIndex(p => (p - 1 + testimonials.length) % testimonials.length);

  return (
    <motion.div className="home-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>

      <HomeHero />

      {/* ==================== FEATURES ==================== */}
      <section className="features-section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="section-header">
            <h2>Powerful Features</h2>
            <p>Everything you need to run your business — payroll, HR, finance, and operations — efficiently and securely.</p>
          </motion.div>
          <div className="features-wrapper">
            <div className="features-grid">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div key={index} className="feature-card" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.08 }} whileHover={{ y: -12, boxShadow: '0 20px 50px rgba(0,0,0,0.08)' }}>
                    <motion.div className="feature-icon-box" style={{ borderColor: feature.color }} whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                      <Icon size={28} color={feature.color} />
                    </motion.div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                    <motion.div className="feature-border" style={{ backgroundColor: feature.color }} layoutId={`border-${index}`} />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SHOWCASE ==================== */}
      <section className="showcase-section">
        <div className="showcase-bg">
          <div className="showcase-blob blob-1" />
          <div className="showcase-blob blob-2" />
        </div>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="section-header">
            <h2>See It in Action</h2>
            <p>A closer look at the tools that power your whole business, not just payroll.</p>
          </motion.div>
          <div className="showcase-layout">
            <motion.div className="showcase-main" initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <div className="showcase-image-frame">
                <div className="showcase-frame-dots"><span /><span /><span /></div>
                <motion.img key={activeImageIndex} src={showcaseImages[activeImageIndex].src} alt={showcaseImages[activeImageIndex].label} className="showcase-img" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} />
                <div className="showcase-image-badge">
                  <span className="badge-dot" />
                  {showcaseImages[activeImageIndex].label}
                </div>
              </div>
              <motion.div key={`desc-${activeImageIndex}`} className="showcase-description" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
                <p>{showcaseImages[activeImageIndex].description}</p>
              </motion.div>
            </motion.div>
            <motion.div className="showcase-thumbs" initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}>
              {showcaseImages.map((img, idx) => (
                <motion.button key={idx} className={`showcase-thumb ${idx === activeImageIndex ? 'active' : ''}`} onClick={() => setActiveImageIndex(idx)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <img src={img.src} alt={img.label} />
                  <div className="thumb-overlay"><span className="thumb-label">{img.label}</span></div>
                  {idx === activeImageIndex && <motion.div className="thumb-active-bar" layoutId="activeThumbBar" />}
                </motion.button>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== PROFESSIONAL BODIES CAROUSEL ==================== */}
      <section className="bodies-section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="section-header">
            <h2>Accreditations & Memberships</h2>
            <p>Independently verified and recognised by the UK's leading professional and regulatory bodies.</p>
          </motion.div>
        </div>
        <div
          className="bodies-track-wrap"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="bodies-fade bodies-fade-left" />
          <div className="bodies-track" ref={carouselRef}>
            {bodiesLoop.map((body, idx) => (
              <a
                key={idx}
                href={body.url}
                target="_blank"
                rel="noopener noreferrer"
                className="body-card"
                aria-label={`Visit ${body.name} website`}
              >
                <div className="body-badge">
                  <img
                    src={body.logo}
                    alt={`${body.name} logo`}
                    className="body-logo-img"
                    loading="lazy"
                  />
                </div>
                <p className="body-name">{body.full}</p>
              </a>
            ))}
          </div>
          <div className="bodies-fade bodies-fade-right" />
        </div>
      </section>


      {/* ==================== VIDEOS ==================== */}
      <section className="videos-section">
        <div className="video-bg" />
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="section-header">
            <h2 style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }}>Payroll Updates</h2>
            <p>Video guides on payroll updates</p>
          </motion.div>
          <div className="videos-grid">
            {videos.map((video, index) => (
              <motion.div key={index} className="video-card" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }} whileHover={{ y: -8 }}>
                <div className="video-wrapper">
                  <iframe width="100%" height="250" src={`https://www.youtube.com/embed/${video.id}`} title={video.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
                <div className="video-content">
                  <h3 style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }}>{video.title}</h3>
                  <p>{video.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section className="testimonials-section">
        <div className="testimonials-bg">
          <div className="testimonials-orb orb-1" />
          <div className="testimonials-orb orb-2" />
        </div>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="section-header text-center">
            <h2>Loved by Businesses and Companies</h2>
            <p>Trusted by teams across the UK</p>
          </motion.div>
          <div className="testimonials-carousel">
            <motion.div key={testimonialsIndex} className="testimonial-card-main" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.5 }}>
              <div className="testimonial-stars">
                {[...Array(testimonials[testimonialsIndex].rating)].map((_, i) => (
                  <motion.span key={i} className="star" animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 0.4, delay: i * 0.08 }}>⭐</motion.span>
                ))}
              </div>
              <p className="testimonial-text">"{testimonials[testimonialsIndex].text}"</p>
              <div className="testimonial-author">
                <div className="author-avatar-main">{testimonials[testimonialsIndex].initials}</div>
                <div className="author-details">
                  <p className="author-name">{testimonials[testimonialsIndex].name}</p>
                  <p className="author-role">{testimonials[testimonialsIndex].company}</p>
                </div>
              </div>
            </motion.div>
            <div className="testimonial-nav">
              <motion.button className="nav-button" onClick={prevTestimonial} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <FiChevronLeft size={20} />
              </motion.button>
              <div className="nav-dots">
                {testimonials.map((_, idx) => (
                  <motion.button key={idx} className={`nav-dot ${idx === testimonialsIndex ? 'active' : ''}`} onClick={() => setTestimonialsIndex(idx)} whileHover={{ scale: 1.2 }} />
                ))}
              </div>
              <motion.button className="nav-button" onClick={nextTestimonial} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <FiChevronRight size={20} />
              </motion.button>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="cta-section">
        <div className="cta-bg"><div className="cta-gradient" /></div>
        <div className="container">
          <motion.div className="cta-content" initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2>Ready to Run Your Whole Business From One Place?</h2>
            <p>Join hundreds of UK businesses simplifying payroll, finance, and operations with Effective Global Staff.</p>
            <motion.div className="cta-buttons" initial={{ opacity: 0, y: 0 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <Link to="/book" className="btn btn-secondary">Book Appointment</Link>
              <Link to="/contact" className="btn btn-light">Contact Sales</Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </motion.div>
  );
};

export default Home;